import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import GammaExposureChart from './components/GammaExposureChart';
import GexDeltaChart from './components/GexDeltaChart';
import DoiChart from './components/DoiChart';
import DoisChart from './components/DoisChart';
import { useGexWebSocket } from './hooks/useGexWebSocket';
import Admin from './Admin';

// API base for login/logout (REST)
const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:4001/api';

// Futures symbols that are NOT supported (commonly confused with stocks)
const UNSUPPORTED_FUTURES = ['ES', 'NQ', 'YM', 'RTY', 'CL', 'GC', 'SI', 'ZB', 'ZN', 'ZC', 'ZS', 'ZW', '6E', '6J'];

// Copy to clipboard button with "Copied" popup
const GexLevelsSummaryWithCopy = ({ symbol, gexLevels }) => {
  const [showCopied, setShowCopied] = useState(false);

  const displaySymbol = ['SPX', 'NDX', 'DJI'].includes(symbol) ? `$${symbol}` : symbol;
  const levels = gexLevels;
  const topStrikes = levels.topGexStrikes || [];

  const parts = [];
  if (levels.callResistance) parts.push(`Call Resistance, ${levels.callResistance}`);
  if (levels.putSupport) parts.push(`Put Support, ${levels.putSupport}`);
  if (levels.hvl) parts.push(`HVL, ${levels.hvl}`);
  if (levels.expected1DMin) parts.push(`1D Min, ${levels.expected1DMin.toFixed(2)}`);
  if (levels.expected1DMax) parts.push(`1D Max, ${levels.expected1DMax.toFixed(2)}`);
  if (levels.callResistance0DTE) parts.push(`Call Resistance 0DTE, ${levels.callResistance0DTE}`);
  if (levels.putSupport0DTE) parts.push(`Put Support 0DTE, ${levels.putSupport0DTE}`);
  if (levels.hvl0DTE) parts.push(`HVL 0DTE, ${levels.hvl0DTE}`);
  if (levels.gammaWall0DTE) parts.push(`Gamma Wall 0DTE, ${levels.gammaWall0DTE}`);
  topStrikes.slice(0, 10).forEach((s, i) => {
    parts.push(`GEX ${i + 1}, ${s.strike}`);
  });

  const summaryText = `${displaySymbol}: ${parts.join(', ')}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(summaryText);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 1000);
  };

  return (
    <div style={copyStyles.container}>
      <div style={copyStyles.buttonWrapper}>
        <button onClick={handleCopy} style={copyStyles.button} title="Copy to clipboard">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        {showCopied && <div style={copyStyles.copiedPopup}>Copied</div>}
      </div>
      {summaryText}
    </div>
  );
};

const copyStyles = {
  container: {
    marginTop: '6px',
    padding: '5px 10px',
    backgroundColor: '#161b22',
    borderRadius: '6px',
    border: '1px solid #30363d',
    fontSize: '10px',
    color: '#c9d1d9',
    fontFamily: 'monospace',
    lineHeight: '1.5',
    wordBreak: 'break-word',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px'
  },
  buttonWrapper: {
    position: 'relative',
    flexShrink: 0
  },
  button: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 4px',
    borderRadius: '4px',
    color: '#8b949e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s'
  },
  copiedPopup: {
    position: 'absolute',
    top: '-24px',
    left: '50%',
    transform: 'translateX(-30%)',
    backgroundColor: '#238636',
    color: '#ffffff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    zIndex: 100,
    animation: 'fadeIn 0.15s ease-out'
  }
};

function App() {
  // User login state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(() => localStorage.getItem('sessionToken'));

  // Session kicked out state
  const [sessionKicked, setSessionKicked] = useState(false);
  const [kickedMessage, setKickedMessage] = useState('');

  // Admin panel state
  const [showAdmin, setShowAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Symbol management
  const [symbol, setSymbol] = useState('SPX');
  const [symbolInput, setSymbolInput] = useState('SPX');
  const [symbolWarning, setSymbolWarning] = useState('');

  // Track symbol change to prevent flash of stale data
  const [isChangingSymbol, setIsChangingSymbol] = useState(false);

  // UI state
  const [showGexLevels, setShowGexLevels] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState('5m');
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Snapshot browsing state
  const [snapshotHistory, setSnapshotHistory] = useState(null);
  const [snapshotIndex, setSnapshotIndex] = useState(null);
  const [frozenGexScale, setFrozenGexScale] = useState(null);

  // GEX level proximity alerts
  const [levelAlerts, setLevelAlerts] = useState([]);
  const dismissedAlerts = useRef(new Set());
  const previousAlertLabels = useRef(new Set());
  const audioCtxRef = useRef(null);

  const playAlertBeep = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.15);
    } catch (e) {
      // AudioContext may not be available
    }
  }, []);

  // Update current time every second for countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Calendar state
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarSelectionMode, setCalendarSelectionMode] = useState('start');
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());

  // Date range - default to 0DTE (today, or Monday if weekend)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    const day = date.getDay();
    // If Saturday (6), add 2 days to get Monday
    // If Sunday (0), add 1 day to get Monday
    if (day === 6) date.setDate(date.getDate() + 2);
    else if (day === 0) date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    const day = date.getDay();
    if (day === 6) date.setDate(date.getDate() + 2);
    else if (day === 0) date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  });

  // Previous data for delta calculations
  const [prevGexData, setPrevGexData] = useState(null);
  const gexDataRef = useRef(null);

  // WebSocket connection - must be before calendar callbacks that use updateDateRange
  const {
    isConnected,
    isAuthenticated: wsAuthenticated,
    gexData: wsGexData,
    vixData,
    vwapData: wsVwapData,
    error: wsError,
    subscribe,
    unsubscribe,
    updateDateRange,
    requestRefresh
  } = useGexWebSocket(sessionToken);

  // Calendar helper functions
  const parseLocalDate = useCallback((dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }, []);

  const dateRangeLabel = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    const startDays = Math.round((start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const endDays = Math.round((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (startDays === endDays) return `${startDays}DTE`;
    return `${startDays}-${endDays}DTE`;
  }, [startDate, endDate, parseLocalDate]);

  const dateDisplayLabel = useMemo(() => {
    if (startDate === endDate) return startDate;
    return `${startDate} to ${endDate}`;
  }, [startDate, endDate]);

  const getDaysInMonth = useCallback((month, year) => new Date(year, month + 1, 0).getDate(), []);
  const getFirstDayOfMonth = useCallback((month, year) => new Date(year, month, 1).getDay(), []);
  const formatDateString = useCallback((year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }, []);

  const handleCalendarDateClick = useCallback((dateStr) => {
    if (calendarSelectionMode === 'start') {
      setStartDate(dateStr);
      setEndDate(dateStr);
      setCalendarSelectionMode('end');
    } else {
      let newStartDate = startDate;
      let newEndDate = dateStr;
      if (dateStr < startDate) {
        newEndDate = startDate;
        newStartDate = dateStr;
      }
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      setCalendarSelectionMode('start');
      setShowCalendar(false);
      // Send new date range to server
      if (symbol) {
        updateDateRange(symbol, newStartDate, newEndDate);
      }
    }
  }, [calendarSelectionMode, startDate, symbol, updateDateRange]);

  const handlePrevMonth = useCallback(() => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  }, [calendarMonth, calendarYear]);

  const handleNextMonth = useCallback(() => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  }, [calendarMonth, calendarYear]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  // Handle WebSocket auth failure - clear local session and show login
  useEffect(() => {
    if (wsError && wsError.includes('Invalid token')) {
      localStorage.removeItem('sessionToken');
      setSessionToken(null);
      setIsLoggedIn(false);
      setShowLoginModal(true);
    }
  }, [wsError]);

  // Current GEX data for the selected symbol
  const gexData = wsGexData[symbol] || null;

  // GEX level proximity detection
  useEffect(() => {
    // Only active when GEX Levels is checked, live mode, and we have data
    if (!showGexLevels || snapshotIndex !== null || !gexData?.gexLevels || !gexData?.underlyingPrice) {
      setLevelAlerts([]);
      return;
    }

    const spot = gexData.underlyingPrice;
    const levels = gexData.gexLevels;
    const THRESHOLD = 0.25; // percent

    const checks = [
      { label: 'HVL 0DTE', level: levels.hvl0DTE },
      { label: 'Put Sup 0DTE', level: levels.putSupport0DTE },
      { label: 'Call Res 0DTE', level: levels.callResistance0DTE },
    ];

    const newAlerts = [];
    const currentLabels = new Set();

    for (const { label, level } of checks) {
      if (!level) continue;
      const distance = Math.abs(spot - level) / spot * 100;
      if (distance <= THRESHOLD) {
        currentLabels.add(label);
        if (!dismissedAlerts.current.has(label)) {
          newAlerts.push({ id: label, label, level, distance });
        }
      } else {
        // Price moved away — allow re-alert if it returns
        dismissedAlerts.current.delete(label);
      }
    }

    // Play beep only for newly appearing alerts
    const prevLabels = previousAlertLabels.current;
    const hasNew = newAlerts.some(a => !prevLabels.has(a.label));
    if (hasNew && newAlerts.length > 0) {
      playAlertBeep();
    }
    previousAlertLabels.current = new Set(newAlerts.map(a => a.label));

    setLevelAlerts(newAlerts);
  }, [gexData, showGexLevels, snapshotIndex, playAlertBeep]);

  // Check user login status on mount
  const checkLoginStatus = useCallback(async () => {
    const token = localStorage.getItem('sessionToken');
    if (token) {
      try {
        const response = await axios.get(`${API_BASE}/login/status`, {
          headers: { 'x-session-token': token }
        });
        if (response.data.loggedIn) {
          setIsLoggedIn(true);
          setShowLoginModal(false);
          setSessionToken(token);
        } else {
          if (response.data.reason === 'session_kicked') {
            setSessionKicked(true);
            setKickedMessage(response.data.message || 'You have been logged out from another location.');
          }
          localStorage.removeItem('sessionToken');
          setIsLoggedIn(false);
          setShowLoginModal(true);
          setSessionToken(null);
        }
      } catch (err) {
        console.error('Login status check failed:', err);
        if (err.response?.data?.code === 'SESSION_KICKED') {
          setSessionKicked(true);
          setKickedMessage(err.response.data.message);
        }
        localStorage.removeItem('sessionToken');
        setIsLoggedIn(false);
        setShowLoginModal(true);
        setSessionToken(null);
      }
    }
  }, []);

  // Handle user login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await axios.post(`${API_BASE}/login`, {
        email: loginEmail,
        password: loginPassword
      });

      if (response.data.success) {
        localStorage.setItem('sessionToken', response.data.token);
        setSessionToken(response.data.token);
        setIsLoggedIn(true);
        setShowLoginModal(false);
        setLoginEmail('');
        setLoginPassword('');
      }
    } catch (err) {
      if (err.response?.data?.code === 'ACCOUNT_DISABLED') {
        setLoginError(err.response.data.message || 'Your account has been disabled.');
      } else {
        setLoginError(err.response?.data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    const token = localStorage.getItem('sessionToken');
    try {
      await axios.post(`${API_BASE}/logout`, {}, {
        headers: { 'x-session-token': token }
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('sessionToken');
    setSessionToken(null);
    setIsLoggedIn(false);
    setShowLoginModal(true);
  };

  // Dismiss kicked modal
  const dismissKickedModal = useCallback(() => {
    setSessionKicked(false);
    setKickedMessage('');
  }, []);

  // Check login on mount
  useEffect(() => {
    checkLoginStatus();
  }, [checkLoginStatus]);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!sessionToken) {
        setIsAdmin(false);
        return;
      }
      try {
        const response = await axios.get(`${API_BASE}/admin/check`, {
          headers: { 'x-session-token': sessionToken }
        });
        setIsAdmin(response.data.isAdmin);
      } catch (err) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [sessionToken]);

  // Subscribe to symbol with date range - subscribe() handles queuing if not yet authenticated
  useEffect(() => {
    if (isConnected && symbol) {
      subscribe(symbol, { startDate, endDate });
      return () => unsubscribe(symbol);
    }
  }, [isConnected, symbol, subscribe, unsubscribe, startDate, endDate]);

  // Track previous data for delta display
  useEffect(() => {
    if (gexData && gexDataRef.current) {
      setPrevGexData(gexDataRef.current);
    }
    gexDataRef.current = gexData;
  }, [gexData]);

  // Clear symbol change flag once valid snapshot data arrives
  useEffect(() => {
    if (isChangingSymbol && gexData?.snapshotDeltas?.[selectedInterval]?.timestamp) {
      setIsChangingSymbol(false);
    }
  }, [isChangingSymbol, gexData, selectedInterval]);

  // Handle symbol change
  const handleSymbolChange = useCallback(() => {
    if (symbolInput && symbolInput !== symbol) {
      const upperSymbol = symbolInput.toUpperCase();

      // Warn about futures symbols (not supported - no futures options)
      if (UNSUPPORTED_FUTURES.includes(upperSymbol)) {
        setSymbolWarning(`"${upperSymbol}" is a futures symbol. Futures options are not supported. For S&P 500, use SPX or SPY.`);
      } else {
        setSymbolWarning('');
      }

      setIsChangingSymbol(true);
      unsubscribe(symbol);
      setSymbol(upperSymbol);
      setPrevGexData(null);
      gexDataRef.current = null;
    }
  }, [symbolInput, symbol, unsubscribe]);

  // Get snapshot timestamp for the selected interval (used to trigger updates only when snapshot changes)
  const snapshotTimestamp = gexData?.snapshotDeltas?.[selectedInterval]?.timestamp;

  // GEX delta data for chart - ONLY updates when snapshot timestamp changes
  const gexDeltaData = useMemo(() => {
    if (!gexData?.gexData) return null;

    // Get per-strike deltas from the selected interval's snapshot
    const snapshotDelta = gexData.snapshotDeltas?.[selectedInterval];
    const gexDeltaByStrike = snapshotDelta?.gexDeltaByStrike || {};

    return gexData.gexData.map(item => ({
      strike: item.strike,
      gexDelta: gexDeltaByStrike[item.strike] || 0
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotTimestamp, selectedInterval]);

  // Shared x-axis scale for both GEX charts (computed from all strikes)
  const gexMaxAbsScale = useMemo(() => {
    if (!gexData?.gexData?.length) return 1;
    return Math.max(...gexData.gexData.map(d => Math.abs(d.totalGex / 1e6))) * 1.1;
  }, [gexData]);

  // Market outlook summary — evaluates GEX, DOI, DOIS, and GEX Delta together
  const marketOutlook = useMemo(() => {
    if (!gexData) return null;

    const gexPositive = gexData.totalGex >= 0;
    const doi = gexData.totalDoi || 0;
    const dois = gexData.dois || 0;
    const gexDeltaPct = gexData.snapshotDeltas?.[selectedInterval]?.totalGexPctChange;
    const deltaAvailable = gexDeltaPct !== undefined && gexDeltaPct !== null;
    const deltaRising = deltaAvailable && gexDeltaPct >= 0;
    const deltaInfo = deltaAvailable
      ? { label: deltaRising ? 'Rising' : 'Falling', pct: gexDeltaPct, color: deltaRising ? '#00ff88' : '#ff4444' }
      : { label: 'N/A', pct: null, color: '#8b949e' };

    // Classify DOI: positive (> threshold), negative (< -threshold), or neutral
    const doiAbs = Math.abs(doi);
    const doiPositive = doi > 0 && doiAbs > 1e4;
    const doiNegative = doi < 0 && doiAbs > 1e4;
    const doiNeutral = !doiPositive && !doiNegative;

    // Classify DOIS: bullish (> +0.3), bearish (< -0.3), or neutral
    const doisBullish = dois > 0.3;
    const doisBearish = dois < -0.3;
    const doisNeutral = !doisBullish && !doisBearish;

    if (gexPositive && doiPositive && doisBullish) {
      if (deltaRising) {
        return { text: 'Strong bullish, low vol — mean reversion favored', color: '#00ff88', delta: deltaInfo };
      }
      return { text: 'Bullish but fading — watch for shift', color: '#ffaa00', delta: deltaInfo };
    }
    if (gexPositive && doiNegative && doisBearish) {
      return { text: 'Bearish bias, but contained — range-bound', color: '#ffaa00', delta: deltaInfo };
    }
    if (!gexPositive && doiPositive && doisBullish) {
      return { text: 'Bullish breakout potential — trend-following favored', color: '#00ff88', delta: deltaInfo };
    }
    if (!gexPositive && doiNegative && doisBearish) {
      return { text: 'Bearish breakdown potential — trend-following favored', color: '#ff4444', delta: deltaInfo };
    }
    if (!gexPositive && doiNeutral && doisNeutral) {
      return { text: 'High volatility regime — expect large moves', color: '#ff4444', delta: deltaInfo };
    }
    if (gexPositive && doiNeutral && doisNeutral) {
      return { text: 'Neutral, low vol — wait for clearer signals', color: '#ffaa00', delta: deltaInfo };
    }

    // Fallback combinations
    if (gexPositive) {
      if (doiPositive || doisBullish) {
        return { text: 'Leaning bullish, low vol — range-bound likely', color: '#00ff88', delta: deltaInfo };
      }
      return { text: 'Low vol regime — mixed signals', color: '#ffaa00', delta: deltaInfo };
    }
    // Negative GEX
    if (doiPositive || doisBullish) {
      return { text: 'Volatile with bullish lean — breakout watch', color: '#ffaa00', delta: deltaInfo };
    }
    if (doiNegative || doisBearish) {
      return { text: 'Volatile with bearish lean — breakdown watch', color: '#ff4444', delta: deltaInfo };
    }
    return { text: 'High vol, uncertain direction — stay nimble', color: '#ffaa00', delta: deltaInfo };
  }, [gexData, selectedInterval]);

  // Freeze scale when entering snapshot mode, clear when exiting
  useEffect(() => {
    if (snapshotIndex !== null && frozenGexScale === null) {
      setFrozenGexScale(gexMaxAbsScale);
    } else if (snapshotIndex === null && frozenGexScale !== null) {
      setFrozenGexScale(null);
    }
  }, [snapshotIndex, gexMaxAbsScale, frozenGexScale]);

  // Fetch snapshots for browsing
  const fetchSnapshots = useCallback(async () => {
    try {
      const params = { interval: selectedInterval };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axios.get(`${API_BASE}/gex/${symbol}/snapshots`, { params });
      return res.data.snapshots || [];
    } catch { return []; }
  }, [symbol, selectedInterval, startDate, endDate]);

  // Refs for snapshot browsing - stable keyboard handler registered once
  const fetchingSnapshots = useRef(false);
  const snapshotIndexRef = useRef(null);
  const snapshotHistoryRef = useRef(null);
  const fetchSnapshotsRef = useRef(fetchSnapshots);

  useEffect(() => { snapshotIndexRef.current = snapshotIndex; }, [snapshotIndex]);
  useEffect(() => { snapshotHistoryRef.current = snapshotHistory; }, [snapshotHistory]);
  useEffect(() => { fetchSnapshotsRef.current = fetchSnapshots; }, [fetchSnapshots]);

  // Reset snapshot browsing on interval/symbol change
  useEffect(() => {
    setSnapshotHistory(null);
    setSnapshotIndex(null);
  }, [selectedInterval, symbol]);

  // Keyboard handler - registered ONCE with capture phase
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      if (!['ArrowLeft', 'ArrowRight', 'Escape'].includes(e.key)) return;

      e.preventDefault();
      e.stopPropagation();

      const idx = snapshotIndexRef.current;
      const history = snapshotHistoryRef.current;

      if (e.key === 'ArrowLeft') {
        if (idx === null) {
          if (fetchingSnapshots.current) return;
          fetchingSnapshots.current = true;
          fetchSnapshotsRef.current().then(snapshots => {
            fetchingSnapshots.current = false;
            if (snapshots.length > 0) {
              setSnapshotHistory(snapshots);
              setSnapshotIndex(snapshots.length - 1);
            }
          });
        } else if (idx > 0) {
          setSnapshotIndex(prev => prev - 1);
        }
      } else if (e.key === 'ArrowRight') {
        if (idx !== null && history) {
          if (idx >= history.length - 1) {
            setSnapshotIndex(null);
            setSnapshotHistory(null);
          } else {
            setSnapshotIndex(prev => prev + 1);
          }
        }
      } else if (e.key === 'Escape') {
        if (idx !== null) {
          setSnapshotIndex(null);
          setSnapshotHistory(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Snapshot navigation callbacks for screen buttons
  const handlePrevSnapshot = useCallback(() => {
    if (snapshotIndex === null) {
      if (fetchingSnapshots.current) return;
      fetchingSnapshots.current = true;
      fetchSnapshotsRef.current().then(snapshots => {
        fetchingSnapshots.current = false;
        if (snapshots.length > 0) {
          setSnapshotHistory(snapshots);
          setSnapshotIndex(snapshots.length - 1);
        }
      });
    } else if (snapshotIndex > 0) {
      setSnapshotIndex(prev => prev - 1);
    }
  }, [snapshotIndex]);

  const handleNextSnapshot = useCallback(() => {
    if (snapshotIndex !== null && snapshotHistory) {
      if (snapshotIndex >= snapshotHistory.length - 1) {
        setSnapshotIndex(null);
        setSnapshotHistory(null);
      } else {
        setSnapshotIndex(prev => prev + 1);
      }
    }
  }, [snapshotIndex, snapshotHistory]);

  // Session Kicked Modal
  const SessionKickedModal = () => {
    if (!sessionKicked) return null;

    return (
      <div style={styles.kickedOverlay}>
        <div style={styles.kickedModal}>
          <div style={styles.kickedIcon}>⚠️</div>
          <h2 style={styles.kickedTitle}>Session Ended</h2>
          <p style={styles.kickedMessage}>{kickedMessage}</p>
          <p style={styles.kickedSubtext}>
            Only one active session is allowed per account for security purposes.
          </p>
          <button onClick={dismissKickedModal} style={styles.kickedButton}>
            Login Again
          </button>
        </div>
      </div>
    );
  };

  // Show Admin Panel
  if (showAdmin && isLoggedIn) {
    return (
      <Admin
        sessionToken={sessionToken}
        onBack={() => setShowAdmin(false)}
      />
    );
  }

  // Login Modal
  if (showLoginModal && !isLoggedIn) {
    return (
      <div style={styles.container}>
        <SessionKickedModal />
        <div style={styles.loginOverlay}>
          <div style={styles.loginModal}>
            <h2 style={styles.loginTitle}>GEX Dashboard</h2>
            <p style={styles.loginSubtitle}>Please enter your credentials to continue</p>

            <form onSubmit={handleLogin} style={styles.loginForm}>
              {loginError && (
                <div style={styles.loginError}>{loginError}</div>
              )}

              <div style={styles.loginField}>
                <label style={styles.loginLabel}>Email</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="Enter your email"
                  style={styles.loginInput}
                  required
                  autoFocus
                />
              </div>

              <div style={styles.loginField}>
                <label style={styles.loginLabel}>Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={styles.loginInput}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loginLoading}
                style={styles.loginButton}
              >
                {loginLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <SessionKickedModal />
      {/* GEX Level Proximity Alert Toasts */}
      {levelAlerts.length > 0 && (
        <div style={styles.alertToastContainer}>
          {levelAlerts.map((alert) => (
            <div key={alert.id} style={styles.alertToast}>
              <div style={styles.alertToastContent}>
                <div style={styles.alertToastLabel}>{alert.label}</div>
                <div style={styles.alertToastValue}>
                  Level: {alert.level} &nbsp;|&nbsp; Distance: {alert.distance.toFixed(2)}%
                </div>
              </div>
              <button
                style={styles.alertToastClose}
                onClick={() => {
                  dismissedAlerts.current.add(alert.label);
                  setLevelAlerts(prev => prev.filter(a => a.id !== alert.id));
                }}
              >
                X
              </button>
            </div>
          ))}
        </div>
      )}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>{symbol} Gamma Exposure</h1>
          {gexData && (
            <div style={styles.priceInfo}>
              <span style={styles.price}>{gexData.underlyingPrice?.toFixed(2)}</span>
              {gexData.openPrice && gexData.underlyingPrice && (() => {
                const change = gexData.underlyingPrice - gexData.openPrice;
                const pct = (change / gexData.openPrice) * 100;
                const isUp = change >= 0;
                return (
                  <span style={{ color: isUp ? '#00ff88' : '#ff4444', fontSize: '14px', marginLeft: '8px' }}>
                    {isUp ? '+' : ''}{change.toFixed(2)} ({isUp ? '+' : ''}{pct.toFixed(2)}%)
                  </span>
                );
              })()}
              <span style={styles.connectionStatus}>
                {isConnected ? (
                  <span style={{ color: '#00ff88' }}>● Live</span>
                ) : (
                  <span style={{ color: '#ff4444' }}>● Disconnected</span>
                )}
              </span>
              {marketOutlook && (
                <span
                  style={{ ...styles.outlookInline, ...styles.tooltipContainer }}
                  onMouseEnter={() => setActiveTooltip('outlook')}
                  onMouseLeave={() => setActiveTooltip(null)}
                >
                  <span style={styles.outlookDivider}>|</span>
                  <span style={{ ...styles.outlookDot, backgroundColor: marketOutlook.color }}></span>
                  <span style={{ color: marketOutlook.color, fontWeight: 600, fontSize: '11px' }}>{marketOutlook.text}</span>
                  <span style={styles.outlookDivider}>|</span>
                  <span style={{ fontSize: '10px', color: '#8b949e' }}>
                    Delta: <span style={{ color: marketOutlook.delta.color, fontWeight: 600 }}>
                      {marketOutlook.delta.label}{marketOutlook.delta.pct !== null ? ` (${marketOutlook.delta.pct >= 0 ? '+' : ''}${marketOutlook.delta.pct.toFixed(1)}%)` : ''}
                    </span>
                  </span>
                  <span style={styles.infoIcon}>?</span>
                  {activeTooltip === 'outlook' && (
                    <div style={{ ...styles.tooltip, width: '420px', left: '0', transform: 'none' }}>
                      <div style={styles.tooltipTitle}>Market Outlook Matrix</div>
                      <div style={styles.tooltipText}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                          <thead>
                            <tr>
                              <th style={styles.outlookTh}>GEX</th>
                              <th style={styles.outlookTh}>DOI</th>
                              <th style={styles.outlookTh}>DOIS</th>
                              <th style={styles.outlookTh}>Delta</th>
                              <th style={styles.outlookTh}>Outlook</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr><td style={styles.outlookTd}>+</td><td style={styles.outlookTd}>+</td><td style={styles.outlookTd}>+</td><td style={styles.outlookTd}>Rising</td><td style={{ ...styles.outlookTd, color: '#00ff88' }}>Strong bullish, mean reversion</td></tr>
                            <tr><td style={styles.outlookTd}>+</td><td style={styles.outlookTd}>+</td><td style={styles.outlookTd}>+</td><td style={styles.outlookTd}>Falling</td><td style={{ ...styles.outlookTd, color: '#ffaa00' }}>Bullish but fading</td></tr>
                            <tr><td style={styles.outlookTd}>+</td><td style={styles.outlookTd}>-</td><td style={styles.outlookTd}>-</td><td style={styles.outlookTd}>Any</td><td style={{ ...styles.outlookTd, color: '#ffaa00' }}>Bearish, contained range</td></tr>
                            <tr><td style={styles.outlookTd}>-</td><td style={styles.outlookTd}>+</td><td style={styles.outlookTd}>+</td><td style={styles.outlookTd}>Any</td><td style={{ ...styles.outlookTd, color: '#00ff88' }}>Bullish breakout</td></tr>
                            <tr><td style={styles.outlookTd}>-</td><td style={styles.outlookTd}>-</td><td style={styles.outlookTd}>-</td><td style={styles.outlookTd}>Any</td><td style={{ ...styles.outlookTd, color: '#ff4444' }}>Bearish breakdown</td></tr>
                            <tr><td style={styles.outlookTd}>-</td><td style={styles.outlookTd}>~0</td><td style={styles.outlookTd}>~0</td><td style={styles.outlookTd}>Any</td><td style={{ ...styles.outlookTd, color: '#ff4444' }}>High vol, large moves</td></tr>
                            <tr><td style={styles.outlookTd}>+</td><td style={styles.outlookTd}>~0</td><td style={styles.outlookTd}>~0</td><td style={styles.outlookTd}>Any</td><td style={{ ...styles.outlookTd, color: '#ffaa00' }}>Neutral, wait for signal</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </span>
              )}
            </div>
          )}
        </div>
        <div style={styles.headerRight}>
          {isLoggedIn && isAdmin && (
            <button onClick={() => setShowAdmin(true)} style={styles.adminButton}>
              Admin
            </button>
          )}
          {isLoggedIn && (
            <button onClick={handleLogout} style={styles.logoutButton}>
              Logout
            </button>
          )}
        </div>
      </header>

      {wsError && (
        <div style={styles.error}>
          {wsError}
        </div>
      )}

      <main style={styles.main}>
        <div style={styles.statsRow}>
              <div style={styles.statCardSymbol}>
                <span style={styles.statLabel}>Ticker</span>
                <div style={styles.symbolInputRow}>
                  <input
                    type="text"
                    value={symbolInput}
                    onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSymbolChange();
                    }}
                    style={styles.symbolInputWide}
                    placeholder="SPX"
                  />
                  <button onClick={handleSymbolChange} style={styles.symbolButtonSmall}>
                    Go
                  </button>
                  <label style={styles.checkboxLabelInline}>
                    <input
                      type="checkbox"
                      checked={showGexLevels}
                      onChange={(e) => setShowGexLevels(e.target.checked)}
                      style={styles.checkbox}
                    />
                    GEX Levels
                  </label>
                </div>
                {symbolWarning && (
                  <div style={styles.symbolWarning}>{symbolWarning}</div>
                )}
              </div>

              <div style={{ ...styles.statCard, ...styles.tooltipContainer, position: 'relative' }}>
                <span style={styles.statLabel}>Exp Range</span>
                <button
                  onClick={() => {
                    setShowCalendar(!showCalendar);
                    setCalendarSelectionMode('start');
                    const today = new Date();
                    setCalendarMonth(today.getMonth());
                    setCalendarYear(today.getFullYear());
                  }}
                  style={styles.calendarButton}
                >
                  {dateDisplayLabel}
                </button>
                <span style={styles.dteLabel}>{dateRangeLabel}</span>

                {showCalendar && (
                  <div style={styles.calendarPopup}>
                    <div style={styles.calendarHeader}>
                      <button onClick={handlePrevMonth} style={styles.calendarNavBtn}>&lt;</button>
                      <span style={styles.calendarMonthYear}>{monthNames[calendarMonth]} {calendarYear}</span>
                      <button onClick={handleNextMonth} style={styles.calendarNavBtn}>&gt;</button>
                    </div>
                    <div style={styles.calendarGrid}>
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                        <div key={day} style={styles.calendarDayHeader}>{day}</div>
                      ))}
                      {(() => {
                        const days = [];
                        const daysInMonth = getDaysInMonth(calendarMonth, calendarYear);
                        const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);
                        const prevMonthDays = getDaysInMonth(
                          calendarMonth === 0 ? 11 : calendarMonth - 1,
                          calendarMonth === 0 ? calendarYear - 1 : calendarYear
                        );

                        // Previous month days
                        for (let i = firstDay - 1; i >= 0; i--) {
                          const day = prevMonthDays - i;
                          const prevMonth = calendarMonth === 0 ? 11 : calendarMonth - 1;
                          const prevYear = calendarMonth === 0 ? calendarYear - 1 : calendarYear;
                          const dateStr = formatDateString(prevYear, prevMonth, day);
                          days.push(
                            <div
                              key={`prev-${day}`}
                              style={{...styles.calendarDay, ...styles.calendarDayOutside}}
                              onClick={() => handleCalendarDateClick(dateStr)}
                            >
                              {day}
                            </div>
                          );
                        }

                        // Current month days
                        const today = new Date();
                        const todayStr = formatDateString(today.getFullYear(), today.getMonth(), today.getDate());

                        for (let day = 1; day <= daysInMonth; day++) {
                          const dateStr = formatDateString(calendarYear, calendarMonth, day);
                          const isSelected = dateStr === startDate || dateStr === endDate;
                          const isInRange = dateStr > startDate && dateStr < endDate;
                          const isToday = dateStr === todayStr;

                          let dayStyle = {...styles.calendarDay};
                          if (isSelected) {
                            dayStyle = {...dayStyle, ...styles.calendarDaySelected};
                          } else if (isInRange) {
                            dayStyle = {...dayStyle, ...styles.calendarDayInRange};
                          }
                          if (isToday && !isSelected) {
                            dayStyle = {...dayStyle, ...styles.calendarDayToday};
                          }

                          days.push(
                            <div
                              key={day}
                              style={dayStyle}
                              onClick={() => handleCalendarDateClick(dateStr)}
                            >
                              {day}
                            </div>
                          );
                        }

                        // Next month days
                        const totalCells = 42;
                        const remainingCells = totalCells - days.length;
                        for (let day = 1; day <= remainingCells; day++) {
                          const nextMonth = calendarMonth === 11 ? 0 : calendarMonth + 1;
                          const nextYear = calendarMonth === 11 ? calendarYear + 1 : calendarYear;
                          const dateStr = formatDateString(nextYear, nextMonth, day);
                          days.push(
                            <div
                              key={`next-${day}`}
                              style={{...styles.calendarDay, ...styles.calendarDayOutside}}
                              onClick={() => handleCalendarDateClick(dateStr)}
                            >
                              {day}
                            </div>
                          );
                        }

                        return days;
                      })()}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={styles.intervalSelector}>
                {['1m', '2m', '3m', '5m', '10m', '15m', '20m'].map((interval) => {
                  const snapshotDelta = gexData?.snapshotDeltas?.[interval];
                  const pctChange = snapshotDelta?.totalGexPctChange;
                  const hasData = pctChange !== undefined && pctChange !== null;

                  return (
                    <div key={interval} style={styles.intervalButtonContainer}>
                      <button
                        onClick={() => setSelectedInterval(interval)}
                        style={{
                          ...styles.intervalButton,
                          ...(selectedInterval === interval ? styles.intervalButtonSelected : {})
                        }}
                      >
                        {interval}
                      </button>
                      <span style={{
                        ...styles.intervalDelta,
                        color: hasData ? (pctChange >= 0 ? '#00ff88' : '#ff4444') : '#8b949e'
                      }}>
                        {hasData ? `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(1)}%` : '--'}
                      </span>
                      <span style={{ ...styles.intervalDelta, color: '#8b949e' }}>
                        {(() => {
                          const intervalMinutes = parseInt(interval.replace('m', ''), 10);
                          const ts = snapshotDelta?.timestamp;
                          if (!ts) return '--';
                          const nextTime = new Date(ts).getTime() + intervalMinutes * 60 * 1000;
                          const secs = Math.max(0, Math.round((nextTime - currentTime) / 1000));
                          return `${secs}s`;
                        })()}
                      </span>
                    </div>
                  );
                })}
              </div>
              </div>

              <div style={styles.statCard}>
                <span style={styles.statLabel}>VIX</span>
                <div style={styles.statValueWithDelta}>
                  <span style={{
                    ...styles.statValue,
                    color: vixData ? (vixData.price >= 20 ? '#ff4444' : vixData.price >= 15 ? '#ffaa00' : '#00ff88') : '#8b949e'
                  }}>
                    {vixData ? vixData.price.toFixed(2) : '--'}
                  </span>
                  {vixData && vixData.change !== undefined && (
                    <span style={{
                      ...styles.deltaIndicator,
                      color: vixData.change >= 0 ? '#ff4444' : '#00ff88'
                    }}>
                      ({vixData.change >= 0 ? '+' : ''}{vixData.change.toFixed(2)})
                    </span>
                  )}
                </div>
              </div>

              <div style={styles.statCard}>
                <span style={styles.statLabel}>
                  VWAP{wsVwapData[symbol]?.isDaily ? ' (D)' : ''}
                </span>
                <div style={styles.statValueWithDelta}>
                  <span style={{
                    ...styles.statValue,
                    color: wsVwapData[symbol] && gexData?.underlyingPrice
                      ? (gexData.underlyingPrice >= wsVwapData[symbol].vwap ? '#00ff88' : '#ff4444')
                      : '#8b949e'
                  }}>
                    {wsVwapData[symbol] ? wsVwapData[symbol].vwap.toFixed(2) : '--'}
                  </span>
                  {wsVwapData[symbol] && gexData?.underlyingPrice && (
                    <span style={{
                      ...styles.deltaIndicator,
                      color: gexData.underlyingPrice >= wsVwapData[symbol].vwap ? '#00ff88' : '#ff4444'
                    }}>
                      ({gexData.underlyingPrice >= wsVwapData[symbol].vwap ? 'Above' : 'Below'})
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{ ...styles.statCard, ...styles.tooltipContainer }}
                onMouseEnter={() => setActiveTooltip('gex')}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                <span style={styles.statLabel}>Total GEX <span style={styles.infoIcon}>?</span></span>
                <div style={styles.statValueWithDelta}>
                  <span style={{
                    ...styles.statValue,
                    color: gexData ? (gexData.totalGex >= 0 ? '#00ff88' : '#ff4444') : '#8b949e'
                  }}>
                    {gexData ? `${gexData.totalGex >= 0 ? '+' : ''}${(gexData.totalGex / 1e9).toFixed(2)}B` : '--'}
                  </span>
                  {gexData && prevGexData?.totalGex !== undefined && (
                    <span style={{
                      ...styles.deltaIndicator,
                      color: (gexData.totalGex - prevGexData.totalGex) >= 0 ? '#00ff88' : '#ff4444'
                    }}>
                      ({(gexData.totalGex - prevGexData.totalGex) >= 0 ? '+' : ''}
                      {((gexData.totalGex - prevGexData.totalGex) / 1e9).toFixed(2)}B)
                    </span>
                  )}
                </div>
                {activeTooltip === 'gex' && (
                  <div style={styles.tooltip}>
                    <div style={styles.tooltipTitle}>Gamma Exposure (GEX)</div>
                    <div style={styles.tooltipText}>
                      <strong>GEX = Gamma x OI x 100 x Spot Price²</strong>
                      <br /><br />
                      Measures aggregate dealer hedging pressure.
                      <br /><br />
                      <span style={{ color: '#00ff88' }}>Positive GEX:</span> Dealers short gamma — they hedge against the trend, stabilizing price. Expect tight ranges, mean-reversion, low volatility.
                      <br /><br />
                      <span style={{ color: '#ff4444' }}>Negative GEX:</span> Dealers long gamma — they hedge with the trend, amplifying moves. Expect breakouts, trending, high volatility.
                      <br /><br />
                      <strong>Key levels:</strong>
                      <br />
                      <strong>Call Resistance</strong> — highest positive GEX above spot (ceiling)
                      <br />
                      <strong>Put Support</strong> — most negative GEX below spot (floor)
                      <br />
                      <strong>HVL</strong> — GEX flip point, key pivot level
                    </div>
                  </div>
                )}
              </div>

              <div
                style={{ ...styles.statCard, ...styles.tooltipContainer }}
                onMouseEnter={() => setActiveTooltip('doi')}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                <span style={styles.statLabel}>Total DOI <span style={styles.infoIcon}>?</span></span>
                <div style={styles.statValueWithDelta}>
                  <span style={{
                    ...styles.statValue,
                    color: gexData ? (gexData.totalDoi >= 0 ? '#00ccff' : '#ff9900') : '#8b949e',
                    fontSize: '14px'
                  }}>
                    {gexData ? `${gexData.totalDoi >= 0 ? '+' : ''}${(gexData.totalDoi / 1e6).toFixed(2)}M` : '--'}
                  </span>
                </div>
                {activeTooltip === 'doi' && (
                  <div style={styles.tooltip}>
                    <div style={styles.tooltipTitle}>Delta-weighted Open Interest (DOI)</div>
                    <div style={styles.tooltipText}>
                      <strong>DOI = Delta x Open Interest x 100</strong>
                      <br /><br />
                      Measures directional exposure in options positioning. Complements GEX — while GEX predicts <em>volatility</em>, DOI predicts <em>direction</em>.
                      <br /><br />
                      <span style={{ color: '#00ccff' }}>Large Positive:</span> Heavy call positioning — bullish directional bias.
                      <br /><br />
                      <span style={{ color: '#ff9900' }}>Large Negative:</span> Heavy put positioning — bearish directional bias.
                      <br /><br />
                      <strong>Near Zero:</strong> Balanced positioning — neutral/uncertain direction.
                    </div>
                  </div>
                )}
              </div>

              <div
                style={{ ...styles.statCard, ...styles.tooltipContainer }}
                onMouseEnter={() => setActiveTooltip('dois')}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                <span style={styles.statLabel}>DOIS (TOS) <span style={styles.infoIcon}>?</span></span>
                <div style={styles.statValueWithDelta}>
                  <span style={{
                    ...styles.statValue,
                    color: gexData ? (gexData.dois >= 0 ? '#00ff88' : '#ff4444') : '#8b949e',
                    fontSize: '14px'
                  }}>
                    {gexData ? `${gexData.dois >= 0 ? '+' : ''}${(gexData.dois * 100).toFixed(1)}%` : '--'}
                  </span>
                </div>
                {activeTooltip === 'dois' && (
                  <div style={styles.tooltip}>
                    <div style={styles.tooltipTitle}>Dealer OI Sentiment (DOIS)</div>
                    <div style={styles.tooltipText}>
                      <strong>DOIS = (Call OI - Put OI) / Total OI</strong>
                      <br /><br />
                      Measures the balance between call and put open interest, ranging from -100% to +100%.
                      <br /><br />
                      <span style={{ color: '#00ff88' }}>Above +30%:</span> Bullish — significantly more calls than puts.
                      <br /><br />
                      <span style={{ color: '#ff4444' }}>Below -30%:</span> Bearish — significantly more puts than calls.
                      <br /><br />
                      <strong>-30% to +30%:</strong> Neutral — relatively balanced positioning.
                      <br /><br />
                      Extreme readings mean one side is crowded, increasing reversal risk. Best signals come when DOI and DOIS confirm each other.
                    </div>
                  </div>
                )}
              </div>

        </div>

        {gexData ? (
          <>
            {/* GEX Levels Bar */}
            {showGexLevels && gexData.gexLevels && (
              <div style={styles.gexLevelsBar}>
                <div style={styles.gexLevelsTitle}>Key GEX Levels</div>
                <div style={styles.gexLevelsGrid}>
                  {(() => {
                    const spot = gexData.underlyingPrice;
                    const levelColor = (level) => level > spot ? '#00ff88' : '#ff4444';
                    return (
                      <>
                        {/* 0DTE Levels */}
                        {gexData.gexLevels.callResistance0DTE && (
                          <div style={styles.gexLevelItem}>
                            <span style={{ ...styles.gexLevelDot, backgroundColor: '#ffa500' }}></span>
                            <span style={styles.gexLevelLabel}>Call Res 0DTE:</span>
                            <span style={{ ...styles.gexLevelValue, color: levelColor(gexData.gexLevels.callResistance0DTE) }}>{gexData.gexLevels.callResistance0DTE}</span>
                          </div>
                        )}
                        {gexData.gexLevels.putSupport0DTE && (
                          <div style={styles.gexLevelItem}>
                            <span style={{ ...styles.gexLevelDot, backgroundColor: '#ffa500' }}></span>
                            <span style={styles.gexLevelLabel}>Put Sup 0DTE:</span>
                            <span style={{ ...styles.gexLevelValue, color: levelColor(gexData.gexLevels.putSupport0DTE) }}>{gexData.gexLevels.putSupport0DTE}</span>
                          </div>
                        )}
                        {gexData.gexLevels.hvl0DTE && (
                          <div style={styles.gexLevelItem}>
                            <span style={{ ...styles.gexLevelDot, backgroundColor: '#ffa500' }}></span>
                            <span style={styles.gexLevelLabel}>HVL 0DTE:</span>
                            <span style={{ ...styles.gexLevelValue, color: levelColor(gexData.gexLevels.hvl0DTE) }}>{gexData.gexLevels.hvl0DTE}</span>
                          </div>
                        )}
                        {gexData.gexLevels.gammaWall0DTE && (
                          <div style={styles.gexLevelItem}>
                            <span style={{ ...styles.gexLevelDot, backgroundColor: '#ffa500' }}></span>
                            <span style={styles.gexLevelLabel}>Gamma Wall 0DTE:</span>
                            <span style={{ ...styles.gexLevelValue, color: levelColor(gexData.gexLevels.gammaWall0DTE) }}>{gexData.gexLevels.gammaWall0DTE}</span>
                          </div>
                        )}
                        {/* Standard Levels */}
                        {gexData.gexLevels.callResistance && (
                          <div style={styles.gexLevelItem}>
                            <span style={{ ...styles.gexLevelDot, backgroundColor: levelColor(gexData.gexLevels.callResistance) }}></span>
                            <span style={styles.gexLevelLabel}>Call Res:</span>
                            <span style={{ ...styles.gexLevelValue, color: levelColor(gexData.gexLevels.callResistance) }}>{gexData.gexLevels.callResistance}</span>
                          </div>
                        )}
                        {gexData.gexLevels.putSupport && (
                          <div style={styles.gexLevelItem}>
                            <span style={{ ...styles.gexLevelDot, backgroundColor: levelColor(gexData.gexLevels.putSupport) }}></span>
                            <span style={styles.gexLevelLabel}>Put Sup:</span>
                            <span style={{ ...styles.gexLevelValue, color: levelColor(gexData.gexLevels.putSupport) }}>{gexData.gexLevels.putSupport}</span>
                          </div>
                        )}
                        {gexData.gexLevels.hvl && (
                          <div style={styles.gexLevelItem}>
                            <span style={{ ...styles.gexLevelDot, backgroundColor: levelColor(gexData.gexLevels.hvl) }}></span>
                            <span style={styles.gexLevelLabel}>HVL:</span>
                            <span style={{ ...styles.gexLevelValue, color: levelColor(gexData.gexLevels.hvl) }}>
                              {gexData.gexLevels.hvl}
                              <span style={{ color: '#8b949e', fontSize: '9px', marginLeft: '4px' }}>
                                ({gexData.gexLevels.distanceToHVL >= 0 ? '+' : ''}{gexData.gexLevels.distanceToHVL?.toFixed(2)}%)
                              </span>
                            </span>
                          </div>
                        )}
                        {gexData.gexLevels.expected1DMin && gexData.gexLevels.expected1DMax && (
                          <div style={styles.gexLevelItem}>
                            <span style={{ ...styles.gexLevelDot, backgroundColor: '#74c0fc' }}></span>
                            <span style={styles.gexLevelLabel}>1D Range:</span>
                            <span style={styles.gexLevelValue}>
                              <span style={{ color: levelColor(gexData.gexLevels.expected1DMin) }}>{gexData.gexLevels.expected1DMin.toFixed(0)}</span>
                              <span> - </span>
                              <span style={{ color: levelColor(gexData.gexLevels.expected1DMax) }}>{gexData.gexLevels.expected1DMax.toFixed(0)}</span>
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                {/* Formatted Levels Text Summary */}
                <GexLevelsSummaryWithCopy symbol={symbol} gexLevels={gexData.gexLevels} />
              </div>
            )}

            <div style={styles.splitContainer}>
              <div style={styles.leftPanel}>
                <GammaExposureChart
                  data={snapshotIndex !== null && snapshotHistory?.[snapshotIndex]?.gexByStrike
                    ? gexData.gexData.map(item => ({
                        strike: item.strike,
                        totalGex: snapshotHistory[snapshotIndex].gexByStrike[item.strike] ?? 0,
                        callGex: snapshotHistory[snapshotIndex].callGexByStrike?.[item.strike] ?? 0,
                        putGex: snapshotHistory[snapshotIndex].putGexByStrike?.[item.strike] ?? 0
                      }))
                    : gexData.gexData}
                  spotPrice={snapshotIndex !== null && snapshotHistory?.[snapshotIndex]
                    ? snapshotHistory[snapshotIndex].underlyingPrice
                    : gexData.underlyingPrice}
                  gexLevels={snapshotIndex !== null ? null : gexData.gexLevels}
                  showGexLevels={snapshotIndex !== null ? false : showGexLevels}
                  fixedMaxAbsGex={snapshotIndex !== null ? (frozenGexScale || gexMaxAbsScale) : null}
                  onPrevSnapshot={handlePrevSnapshot}
                  onNextSnapshot={handleNextSnapshot}
                  snapshotLabel={snapshotIndex !== null && snapshotHistory?.[snapshotIndex]
                    ? `${new Date(snapshotHistory[snapshotIndex].timestamp).toLocaleTimeString()} (${snapshotIndex + 1}/${snapshotHistory.length})`
                    : null}
                />
              </div>
              <div style={styles.rightPanel}>
                {!isChangingSymbol && (
                  <GexDeltaChart
                    data={snapshotIndex !== null && snapshotHistory?.[snapshotIndex]?.gexDeltaByStrike
                      ? gexData.gexData.map(item => ({
                          strike: item.strike,
                          gexDelta: snapshotHistory[snapshotIndex].gexDeltaByStrike[item.strike] || 0
                        }))
                      : gexDeltaData}
                    spotPrice={snapshotIndex !== null && snapshotHistory?.[snapshotIndex]
                      ? snapshotHistory[snapshotIndex].underlyingPrice
                      : (gexData.snapshotDeltas?.[selectedInterval]?.underlyingPrice || gexData.underlyingPrice)}
                    interval={selectedInterval}
                    pctChange={snapshotIndex !== null && snapshotHistory?.[snapshotIndex]
                      ? snapshotHistory[snapshotIndex].totalGexPctChange
                      : gexData.snapshotDeltas?.[selectedInterval]?.totalGexPctChange}
                    totalGexDelta={snapshotIndex !== null && snapshotHistory?.[snapshotIndex]
                      ? snapshotHistory[snapshotIndex].totalGexDelta
                      : gexData.snapshotDeltas?.[selectedInterval]?.totalGexDelta}
                  />
                )}
              </div>
            </div>

            <div style={styles.splitContainer}>
              <div style={styles.leftPanel}>
                <DoiChart
                  data={gexData.doiData}
                  spotPrice={gexData.underlyingPrice}
                />
              </div>
              <div style={styles.rightPanel}>
                <DoisChart
                  data={gexData.doisData}
                  spotPrice={gexData.underlyingPrice}
                />
              </div>
            </div>
          </>
        ) : (
          <div style={styles.loading}>
            {isConnected ? (
              wsAuthenticated ? `Waiting for data for ${symbol}...` : 'Authenticating...'
            ) : (
              'Connecting to server...'
            )}
          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0d1117',
    color: '#e6e6e6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  loginOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  loginModal: {
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '1px solid #30363d',
    padding: '40px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
  },
  loginTitle: {
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: 600,
    color: '#ffffff',
    textAlign: 'center'
  },
  loginSubtitle: {
    margin: '0 0 32px 0',
    fontSize: '14px',
    color: '#8b949e',
    textAlign: 'center'
  },
  loginForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  loginField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  loginLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#c9d1d9'
  },
  loginInput: {
    padding: '12px 14px',
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    color: '#e6e6e6',
    fontSize: '14px',
    outline: 'none'
  },
  loginButton: {
    marginTop: '8px',
    padding: '14px 20px',
    backgroundColor: '#238636',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 600
  },
  loginError: {
    padding: '12px 14px',
    backgroundColor: '#3d1a1a',
    border: '1px solid #6e3630',
    borderRadius: '6px',
    color: '#ff7b72',
    fontSize: '14px',
    textAlign: 'center'
  },
  kickedOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000
  },
  kickedModal: {
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '2px solid #f85149',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 8px 32px rgba(248, 81, 73, 0.3)',
    textAlign: 'center'
  },
  kickedIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  kickedTitle: {
    margin: '0 0 12px 0',
    fontSize: '24px',
    fontWeight: 600,
    color: '#f85149'
  },
  kickedMessage: {
    margin: '0 0 12px 0',
    fontSize: '15px',
    color: '#e6e6e6',
    lineHeight: 1.5
  },
  kickedSubtext: {
    margin: '0 0 24px 0',
    fontSize: '13px',
    color: '#8b949e'
  },
  kickedButton: {
    padding: '14px 32px',
    backgroundColor: '#238636',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 600
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px',
    borderBottom: '1px solid #30363d',
    backgroundColor: '#161b22'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  title: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 600,
    color: '#ffffff'
  },
  priceInfo: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px'
  },
  price: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#ffffff'
  },
  connectionStatus: {
    fontSize: '11px',
    fontWeight: 500
  },
  refreshInfo: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
    fontSize: '10px',
    color: '#8b949e'
  },
  lastUpdate: {},
  nextRefresh: {
    color: '#58a6ff'
  },
  refreshButton: {
    padding: '2px 6px',
    backgroundColor: '#21262d',
    color: '#58a6ff',
    border: '1px solid #30363d',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    lineHeight: 1,
    transition: 'all 0.2s ease'
  },
  logoutButton: {
    padding: '4px 10px',
    backgroundColor: '#21262d',
    color: '#c9d1d9',
    border: '1px solid #30363d',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 500,
    marginLeft: '6px'
  },
  adminButton: {
    padding: '4px 10px',
    backgroundColor: '#1f6feb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 500
  },
  main: {
    padding: '24px'
  },
  error: {
    margin: '16px 24px',
    padding: '12px 16px',
    backgroundColor: '#3d1a1a',
    border: '1px solid #6e3630',
    borderRadius: '6px',
    color: '#ff7b72'
  },
  statsRow: {
    display: 'flex',
    gap: '7px',
    marginBottom: '10px'
  },
  statCard: {
    flex: 1,
    padding: '5px 10px',
    backgroundColor: '#161b22',
    borderRadius: '5px',
    border: '1px solid #30363d',
    display: 'flex',
    flexDirection: 'column',
    gap: '1px'
  },
  intervalSelector: {
    display: 'flex',
    gap: '4px',
    alignItems: 'flex-start',
    padding: '5px 10px',
    backgroundColor: '#161b22',
    borderRadius: '5px',
    border: '1px solid #30363d'
  },
  intervalButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px'
  },
  intervalButton: {
    width: '32px',
    height: '32px',
    padding: 0,
    backgroundColor: '#21262d',
    color: '#c9d1d9',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: '#6e7681',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  intervalButtonSelected: {
    borderColor: '#238636',
    color: '#3fb950'
  },
  intervalDelta: {
    fontSize: '9px',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  dteLabel: {
    fontSize: '10px',
    color: '#c9a227',
    fontWeight: 600
  },
  calendarButton: {
    padding: '4px 8px',
    backgroundColor: '#1a1a2e',
    border: '1px solid #30363d',
    borderRadius: '4px',
    color: '#ffffff',
    fontSize: '10px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%'
  },
  calendarPopup: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: '#1a1a2e',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '8px',
    zIndex: 1000,
    minWidth: '240px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
  },
  calendarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    padding: '0 4px'
  },
  calendarNavBtn: {
    background: 'none',
    border: 'none',
    color: '#8b949e',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px'
  },
  calendarMonthYear: {
    color: '#c9a227',
    fontSize: '13px',
    fontWeight: '500'
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '2px'
  },
  calendarDayHeader: {
    color: '#8b949e',
    fontSize: '10px',
    textAlign: 'center',
    padding: '4px 0',
    fontWeight: '500'
  },
  calendarDay: {
    color: '#ffffff',
    fontSize: '11px',
    textAlign: 'center',
    padding: '6px 4px',
    cursor: 'pointer',
    borderRadius: '4px',
    transition: 'background-color 0.15s'
  },
  calendarDayOutside: {
    color: '#4a4a5a'
  },
  calendarDaySelected: {
    backgroundColor: '#c9a227',
    color: '#000000',
    fontWeight: 'bold'
  },
  calendarDayInRange: {
    backgroundColor: 'rgba(201, 162, 39, 0.3)',
    color: '#ffffff'
  },
  calendarDayToday: {
    border: '1px solid #c9a227'
  },
  statCardSymbol: {
    flex: '0 0 auto',
    width: '185px',
    padding: '5px 10px',
    backgroundColor: '#161b22',
    borderRadius: '5px',
    border: '1px solid #30363d',
    display: 'flex',
    flexDirection: 'column',
    gap: '1px'
  },
  statLabel: {
    fontSize: '9px',
    color: '#8b949e',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  statValue: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#ffffff'
  },
  statValueWithDelta: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
    flexWrap: 'wrap'
  },
  deltaIndicator: {
    fontSize: '10px',
    fontWeight: 500
  },
  symbolInputRow: {
    display: 'flex',
    gap: '5px',
    marginTop: '2px'
  },
  symbolInputWide: {
    padding: '4px 6px',
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '4px',
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: 600,
    width: '55px'
  },
  symbolWarning: {
    marginTop: '4px',
    padding: '4px 8px',
    backgroundColor: '#3d2f00',
    border: '1px solid #6e5a00',
    borderRadius: '4px',
    color: '#ffcc00',
    fontSize: '10px',
    lineHeight: '1.3'
  },
  symbolButtonSmall: {
    padding: '4px 8px',
    backgroundColor: '#238636',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '10px',
    fontWeight: 600
  },
  checkboxLabelInline: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    color: '#8b949e',
    fontSize: '9px',
    cursor: 'pointer',
    marginLeft: '5px'
  },
  checkbox: {
    cursor: 'pointer',
    accentColor: '#238636'
  },
  tooltipContainer: {
    position: 'relative',
    cursor: 'help'
  },
  infoIcon: {
    display: 'inline-block',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    backgroundColor: '#30363d',
    color: '#8b949e',
    fontSize: '10px',
    textAlign: 'center',
    lineHeight: '14px',
    marginLeft: '4px',
    verticalAlign: 'middle'
  },
  tooltip: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginTop: '8px',
    padding: '16px',
    backgroundColor: '#1c2128',
    border: '1px solid #30363d',
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    zIndex: 1000,
    width: '280px',
    textAlign: 'left'
  },
  tooltipTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '8px',
    borderBottom: '1px solid #30363d',
    paddingBottom: '8px'
  },
  tooltipText: {
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: 1.5
  },
  priceExpectation: {
    fontSize: '14px',
    fontWeight: 600,
    marginLeft: '12px',
    padding: '4px 10px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255,255,255,0.1)'
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: '#8b949e',
    fontSize: '16px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px',
    padding: '24px'
  },
  symbolInputStandalone: {
    backgroundColor: '#161b22',
    borderRadius: '8px',
    border: '1px solid #30363d',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  splitContainer: {
    display: 'flex',
    gap: '24px',
    width: '100%',
    marginBottom: '24px'
  },
  leftPanel: {
    flex: 1,
    minWidth: 0
  },
  rightPanel: {
    flex: 1,
    minWidth: 0
  },
  gexLevelsBar: {
    padding: '6px 12px',
    backgroundColor: '#161b22',
    borderRadius: '8px',
    border: '1px solid #30363d',
    marginBottom: '12px'
  },
  gexLevelsTitle: {
    fontSize: '10px',
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '4px'
  },
  gexLevelsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px 18px',
    alignItems: 'center'
  },
  gexLevelItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: '10px'
  },
  gexLevelDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0
  },
  gexLevelLabel: {
    color: '#8b949e'
  },
  gexLevelValue: {
    color: '#ffffff',
    fontWeight: 600
  },
  gexLevelsInline: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px 16px',
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #30363d',
    fontSize: '11px'
  },
  gexLevelCompactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px'
  },
  gexLevelCompactLabel: {
    color: '#8b949e'
  },
  gexLevelCompactValue: {
    fontWeight: 600
  },
  snapshotTimestamp: {
    fontSize: '12px',
    color: '#58a6ff',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  outlookInline: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    position: 'relative'
  },
  outlookDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0
  },
  outlookDivider: {
    color: '#30363d',
    fontSize: '14px',
    margin: '0 4px'
  },
  outlookTh: {
    padding: '4px 6px',
    borderBottom: '1px solid #30363d',
    color: '#c9d1d9',
    fontWeight: 600,
    textAlign: 'left'
  },
  outlookTd: {
    padding: '3px 6px',
    borderBottom: '1px solid rgba(48,54,61,0.5)',
    color: '#8b949e'
  },
  alertToastContainer: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxWidth: '320px'
  },
  alertToast: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderLeft: '4px solid #ffa500',
    borderRadius: '6px',
    padding: '10px 12px',
    boxShadow: '0 4px 16px rgba(255, 165, 0, 0.2)',
    animation: 'fadeIn 0.2s ease-out'
  },
  alertToastContent: {
    flex: 1
  },
  alertToastLabel: {
    color: '#ffa500',
    fontSize: '12px',
    fontWeight: 700,
    marginBottom: '2px'
  },
  alertToastValue: {
    color: '#8b949e',
    fontSize: '11px'
  },
  alertToastClose: {
    background: 'none',
    border: 'none',
    color: '#484f58',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 700,
    padding: '4px 6px',
    marginLeft: '8px',
    borderRadius: '4px'
  },
};

export default App;
